import React, { useState } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { getLanguageBadge } from '../../utils/mailto';

const NAV_ITEMS = [
  { id: 'inicio', key: 'home' },
  { id: 'servicios', key: 'services' },
  { id: 'precios', key: 'pricing' },
  { id: 'sobre-mi', key: 'about' },
];

const LANGS = [
  { code: 'es', label: '🇪🇸 Español' },
  { code: 'en', label: '🇬🇧 English' },
  { code: 'cs', label: '🇨🇿 Čeština' },
];

const Navigation = ({ t, language, setLanguage }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const selectLanguage = (code) => {
    setLanguage(code);
    setShowLanguageMenu(false);
    setMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0">
            <h1 className="text-2xl font-bold text-gray-900">
              <span className="text-blue-600">Pulso</span> Media Agency
            </h1>
          </div>

          {/* Desktop */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  data-testid={`nav-${item.id}-btn`}
                >
                  {t.nav[item.key]}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('contacto')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                data-testid="nav-contact-btn"
              >
                {t.nav.contact}
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                  className="flex items-center gap-2 text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  data-testid="language-toggle-btn"
                >
                  <Globe className="w-4 h-4" />
                  {getLanguageBadge(language)}
                </button>
                {showLanguageMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    {LANGS.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => selectLanguage(lang.code)}
                        className={`block w-full text-left px-4 py-2 text-sm ${language === lang.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`}
                        data-testid={`lang-option-${lang.code}`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile button */}
          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className="text-gray-700 hover:text-blue-600 p-2"
              data-testid="mobile-language-btn"
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-blue-600"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left text-gray-700 hover:text-blue-600 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                data-testid={`mobile-nav-${item.id}-btn`}
              >
                {t.nav[item.key]}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('contacto')}
              className="block w-full text-left bg-blue-600 text-white px-3 py-2 rounded-md text-base font-medium hover:bg-blue-700"
              data-testid="mobile-nav-contact-btn"
            >
              {t.nav.contact}
            </button>

            <div className="border-t pt-2 mt-2">
              {LANGS.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => selectLanguage(lang.code)}
                  className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium ${language === lang.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
                  data-testid={`mobile-lang-option-${lang.code}`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
